import { Team } from "../types";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Calendar, Mail, MessageCircle, Users, FileText, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { useTeamMessages } from "@/pages/applications/components/TeamBlog/useTeamMessages";
import { AverageTeamCost } from '@/components/common/AverageTeamCost';
import { TeamEquipmentCost } from '@/components/common/TeamEquipmentCost';
import { TeamGuardEquipmentCost } from '@/components/common/TeamGuardEquipmentCost';
import { TeamTrienniaDisplay } from '@/components/common/TeamTrienniaDisplay';
import { StatusBadge } from '@/components/ui/status-badge';
import { useRealTimeStats } from '@/hooks/useRealTimeStats';
import { cn } from "@/lib/utils";
interface TeamCardProps {
  team: Team;
  onDeleteTeam: (team: Team) => Promise<void>;
  isAdmin: boolean;
}
export const TeamCard = ({
  team,
  onDeleteTeam,
  isAdmin
}: TeamCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    toast
  } = useToast();
  const {
    unreadCount: teamUnreadCount
  } = useTeamMessages(team.id);
  const {
    stats
  } = useRealTimeStats([team.id]);
  const teamStats = stats[team.id];

  // Generate subtle variations based on team ID for visual diversity
  const teamVariation = team.id.charCodeAt(0) % 6;
  const getBorderColor = () => {
    switch (teamVariation) {
      case 0:
        return "border-blue-500 dark:border-blue-400";
      case 1:
        return "border-purple-500 dark:border-purple-400";
      case 2:
        return "border-emerald-500 dark:border-emerald-400";
      case 3:
        return "border-orange-500 dark:border-orange-400";
      case 4:
        return "border-pink-500 dark:border-pink-400";
      case 5:
        return "border-teal-500 dark:border-teal-400";
      default:
        return "border-blue-500 dark:border-blue-400";
    }
  };
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDeleteTeam(team);
    } finally {
      setIsDeleting(false);
    }
  };
  const handleBulkEmail = async () => {
    try {
      const {
        data: teamMembers,
        error
      } = await supabase.from('team_members').select('user_id').eq('team_id', team.id);
      if (error) {
        console.error("Error fetching team members:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not fetch team members"
        });
        return;
      }
      if (!teamMembers || teamMembers.length === 0) {
        toast({
          title: "No team members",
          description: "This team has no members"
        });
        return;
      }
      const profileIds = teamMembers.map(member => member.user_id);
      const {
        data: profiles,
        error: profilesError
      } = await supabase.from('profiles').select('email').in('id', profileIds);
      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not fetch team members' emails"
        });
        return;
      }
      const emails = profiles.map(profile => profile.email).filter(email => email).join(',');
      if (!emails.length) {
        toast({
          title: "No emails found",
          description: "There are no team members with email addresses"
        });
        return;
      }
      window.location.href = `mailto:${emails}?subject=Message for ${team.name} Team`;
    } catch (err) {
      console.error("Error handling bulk email:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred"
      });
    }
  };
  return <Card className={cn("relative overflow-hidden group transform transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:-translate-y-2 rounded-2xl border-2 bg-background/50", getBorderColor(), "shadow-lg hover:shadow-xl")}>
      
      <CardHeader className="relative pb-2 pt-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-foreground/10 backdrop-blur-sm border border-border/40 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 overflow-hidden">
              {team.image_url ? <img src={team.image_url} alt={`${team.name} logo`} className="w-full h-full object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }} /> : null}
              <FileText className={`w-6 h-6 text-muted-foreground ${team.image_url ? 'hidden' : ''}`} />
            </div>
            <CardTitle className="text-xl font-bold text-foreground drop-shadow-sm group-hover:text-primary transition-colors">
              {team.name}
            </CardTitle>
          </div>
        </div>
        <CardDescription className="line-clamp-2 h-10 text-muted-foreground leading-relaxed drop-shadow-sm">
          {team.description || "No description provided."}
        </CardDescription>
        
        {/* Real-time status badges */}
        {teamStats && <div className="flex flex-wrap gap-2 mt-3">
            <StatusBadge type="active-guards" count={teamStats.activeGuards} size="sm" />
            <StatusBadge type="pending-reports" count={teamStats.pendingReports} size="sm" />
            <StatusBadge type="active-shifts" count={teamStats.activeShifts} size="sm" />
          </div>}
      </CardHeader>
      
      <CardContent className="relative pb-4">
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="flex items-center gap-2 text-sm text-foreground bg-background/40 dark:bg-background/60 rounded-lg p-2 backdrop-blur-sm border border-border">
            <div className="w-8 h-8 rounded-lg bg-background/40 dark:bg-background/60 flex items-center justify-center">
              <Users size={16} className="text-muted-foreground drop-shadow-sm" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-foreground drop-shadow-sm">{team.team_members_count || 0}</span>
              <span className="text-xs text-muted-foreground drop-shadow-sm">Members</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-foreground bg-background/40 dark:bg-background/60 rounded-lg p-2 backdrop-blur-sm border border-border">
            <div className="w-8 h-8 rounded-lg bg-background/40 dark:bg-background/60 flex items-center justify-center overflow-hidden">
              {team.image_url ? <img src={team.image_url} alt={`${team.name} logo`} className="w-4 h-4 object-contain rounded" onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }} /> : null}
              <FileText size={16} className={`text-muted-foreground drop-shadow-sm ${team.image_url ? 'hidden' : ''}`} />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-foreground drop-shadow-sm">{team.reports_count || 0}</span>
              <span className="text-xs text-muted-foreground drop-shadow-sm">Reports</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-foreground bg-background/40 dark:bg-background/60 rounded-lg p-2 backdrop-blur-sm border border-border">
            <div className="w-8 h-8 rounded-lg bg-background/40 dark:bg-background/60 flex items-center justify-center">
              <Calendar size={16} className="text-muted-foreground drop-shadow-sm" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-foreground drop-shadow-sm">{team.missions_count || 0}</span>
              <span className="text-xs text-muted-foreground drop-shadow-sm">Missions</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-foreground bg-background/40 dark:bg-background/60 rounded-lg p-2 backdrop-blur-sm border border-border">
            <div className="w-8 h-8 rounded-lg bg-background/40 dark:bg-background/60 flex items-center justify-center">
              <AlertCircle size={16} className="text-muted-foreground drop-shadow-sm" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-foreground drop-shadow-sm">{team.applications_count || 0}</span>
              <span className="text-xs text-muted-foreground drop-shadow-sm">Applications</span>
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="relative pt-0 flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2 w-full">
          <Link to={`/reports/team/${team.id}`} className="w-full">
            <Button variant="outline" className="w-full bg-background/25 dark:bg-background/60 border-border text-foreground hover:bg-background hover:text-foreground backdrop-blur-sm transition-all duration-300 font-medium">
              Reports
            </Button>
          </Link>
          <Link to={`/missions/team/${team.id}`} className="w-full">
            <Button variant="outline" className="w-full bg-background/25 dark:bg-background/60 border-border text-foreground hover:bg-background hover:text-foreground backdrop-blur-sm transition-all duration-300 font-medium">
              Missions
            </Button>
          </Link>
        </div>
        
        <Link to="/leave" className="w-full">
          <Button variant="outline" className="w-full bg-background/25 dark:bg-background/60 border-border text-foreground hover:bg-background hover:text-foreground backdrop-blur-sm transition-all duration-300 font-medium">
            Applications
          </Button>
        </Link>
        
        <Link to={`/applications/team/${team.id}/chat`} className="w-full">
          <Button variant="outline" className={cn("w-full flex items-center justify-center relative backdrop-blur-sm transition-all duration-300 font-medium", teamUnreadCount > 0 ? 'bg-gradient-to-r from-amber-500 to-amber-600 border-amber-400 text-white hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-xl ring-2 ring-amber-300/50' : 'bg-background/25 dark:bg-background/60 border-border text-foreground hover:bg-background hover:text-foreground')}>
            <MessageCircle size={16} className="mr-2" />
            Chat
            {teamUnreadCount > 0 && <Badge variant="destructive" className="ml-1 w-5 h-5 flex items-center justify-center p-0 text-[10px] rounded-full bg-red-500 text-white animate-pulse shadow-lg">
                {teamUnreadCount > 99 ? '99+' : teamUnreadCount}
              </Badge>}
          </Button>
        </Link>
        
        <Button variant="outline" className="w-full bg-background/25 dark:bg-background/60 border-border text-foreground hover:bg-background hover:text-foreground backdrop-blur-sm transition-all duration-300 flex items-center justify-center font-medium" onClick={handleBulkEmail}>
          <Mail size={16} className="mr-2" />
          Team Bulk Email
        </Button>
        
        {isAdmin && <>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full mt-2 bg-red-600/90 hover:bg-red-700 border-red-500/50 backdrop-blur-sm transition-all duration-300 font-medium shadow-lg" disabled={isDeleting}>
                  <Trash2 size={16} className="mr-2" />
                  {isDeleting ? "Deleting..." : "Delete Team"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Team: {team.name}</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>This action cannot be undone. This will permanently delete:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>The team "{team.name}"</li>
                      <li>All team members and assignments</li>
                      <li>All schedules and shift templates</li>
                      <li>All reports and submissions</li>
                      <li>All missions and applications</li>
                      <li>All notifications and messages</li>
                      <li>All related data and dependencies</li>
                    </ul>
                    <p className="font-semibold text-red-600 mt-3">
                      Are you absolutely sure you want to proceed?
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={isDeleting}>
                    {isDeleting ? "Deleting..." : "Delete Team & All Data"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          
            <div className="w-full border-t border-border/30 mt-3 pt-3">
              <div className="grid grid-cols-2 gap-2 w-full">
                <div className="col-span-1 flex justify-center">
                  <AverageTeamCost teamId={team.id} />
                </div>
                <div className="col-span-1 flex justify-center">
                  <TeamEquipmentCost teamId={team.id} />
                </div>
                <div className="col-span-1 flex justify-center">
                  <TeamGuardEquipmentCost teamId={team.id} />
                </div>
                <div className="col-span-1 flex justify-center">
                  <TeamTrienniaDisplay teamId={team.id} />
                </div>
              </div>
            </div>
          </>}
        
        {/* Arrow indicator */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1">
          
        </div>
      </CardFooter>
    </Card>;
};